import { applyInnerTen as applyInnerTenRule } from "../lib/scoring";

/**
 * useScoreEntry — local score-entry operations for the in-progress end.
 *
 * Mechanical extraction of placeArrow / pushScore / relocateArrow / undoLast /
 * applyInnerTen from Score.jsx. Behavior is preserved verbatim: each mutation
 * flips localChangeRef so the live-sync path can suppress server state from
 * racing back over the user's edit.
 */
export function useScoreEntry({ round, current, setCurrent, localChangeRef, arrowsPerEnd, allEndsDone, editingEnd }) {
  const arrowIdx = current.arrows.length;

  // When inner-ring scoring is on, the outer 10-ring scores as 9 — only X scores
  // 10pts. The rule itself lives in the scoring ENGINE (lib/scoring.js
  // applyInnerTen, mirrored by backend/app/scoring.py, #1868) — this is just the
  // round-flag binding for the entry paths below.
  //
  // The round's FACE is bound here too (#2172). `round.inner_ten` is set from
  // bow style with no round-type gate, so it is true for a compound archer on a
  // 3D round; rewriting their 10-ring hit to "9" produced a score an ASA/IBO
  // animal cannot hold and the confirm 422'd. The engine decides whether the
  // face has the rings — the hook only supplies which face is being shot.
  const applyInnerTen = (score) =>
    applyInnerTenRule(score, Boolean(round?.inner_ten), round?.target_face ?? null);

  function placeArrow(payload) {
    // A full round blocks new ends — but editing an earlier end is still allowed.
    if (arrowIdx >= arrowsPerEnd || (allEndsDone && editingEnd == null)) return;
    localChangeRef.current = true;
    setCurrent({
      arrows: [...current.arrows, applyInnerTen(payload.score)],
      positions: [...current.positions, { x: payload.x, y: payload.y }],
    });
  }

  // Keypad entry carries no target position (#1837). Synthesising one here
  // used to persist a random-angle x/y indistinguishable from a real tap,
  // which leaked into Replay / grouping / shot-pattern / heatmap history —
  // meaningless whole-face coordinates on multi-spot faces. Null keeps the
  // arrow score-only: it counts everywhere, renders on no location surface.
  function pushScore(s) {
    placeArrow({ x: null, y: null, score: s });
  }

  // Drag a placed arrow to a new spot — its score recomputes from the new
  // coordinates (the Target resolves them through hitZone before calling).
  function relocateArrow(index, payload) {
    localChangeRef.current = true;
    setCurrent((c) => ({
      arrows: c.arrows.map((s, i) => (i === index ? applyInnerTen(payload.score) : s)),
      positions: c.positions.map((p, i) =>
        i === index ? { x: payload.x, y: payload.y } : p,
      ),
    }));
  }

  function undoLast() {
    if (current.arrows.length === 0) return;
    localChangeRef.current = true;
    setCurrent({
      arrows: current.arrows.slice(0, -1),
      positions: current.positions.slice(0, -1),
    });
  }

  return { arrowIdx, applyInnerTen, placeArrow, pushScore, relocateArrow, undoLast };
}
